import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { businessUnitsApi } from '@/lib/api';
import { UNIT_ACCESS_LABEL, useCanManageUnits } from '@/lib/business-units';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { ChangePrimaryEmployerDialog } from './change-primary-employer-dialog';
import { GrantUnitAccessDialog } from './grant-unit-access-dialog';
import { Section } from './section';
import { RevokeUnitAccessButton, UnitRoleCell } from './unit-access-actions';

interface Props {
  user: { id: string; name: string; businessUnitId: string };
  /** Show where they work without the controls that change it (the edit
   *  form; changes are made on the employee's page). */
  readOnly?: boolean;
}

/**
 * The business units one employee works in — their primary employer, the
 * units they were granted, or every unit when their role holds All Business
 * Units — and their
 * role in each. Each change is its own operation: change a role, remove a
 * grant, grant another unit, change the primary employer.
 *
 * Shown to callers who can see business units (Setup → Business units).
 */
export function UserBusinessUnitsSection({ user, readOnly = false }: Props) {
  const canView = useHasPermission('business_unit.view');
  const canManageUnits = useCanManageUnits() && !readOnly;
  const [granting, setGranting] = useState(false);
  const [moving, setMoving] = useState(false);
  const units = useQuery({
    queryKey: queryKeys.businessUnits.ofUser(user.id),
    queryFn: () => businessUnitsApi.ofUser(user.id),
    enabled: canView,
  });
  if (!canView) return null;

  const rows = units.data?.data ?? [];
  // Their role already reaches every unit: there is nothing to grant.
  const reachesAllUnits = rows.some((u) => u.access === 'all-units');
  return (
    <Section
      title="Business Units"
      description={
        readOnly
          ? "Where they work, and their role in each — changed on the employee's page."
          : 'Where they work, and their role in each.'
      }
      count={units.data ? rows.length : undefined}
      headerRight={
        canManageUnits ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setMoving(true)}>
              Change primary employer
            </Button>
            {reachesAllUnits ? null : (
              <Button size="sm" onClick={() => setGranting(true)}>
                Grant access
              </Button>
            )}
          </div>
        ) : null
      }
    >
      {units.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : units.isError ? (
        <p className="text-sm text-destructive">
          {units.error instanceof ApiError ? units.error.message : 'Failed to load Business Units.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business Unit</TableHead>
              <TableHead>Access</TableHead>
              <TableHead>Role</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <Link
                    to="/setup/business-units/$id"
                    params={{ id: u.id }}
                    className="font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {u.name}
                  </Link>{' '}
                  <span className="text-xs text-muted-foreground">{u.code}</span>
                </TableCell>
                <TableCell>
                  <Badge variant={u.access === 'primary' ? 'default' : 'secondary'}>
                    {UNIT_ACCESS_LABEL[u.access]}
                  </Badge>
                </TableCell>
                <TableCell>
                  {readOnly ? (
                    <span className="text-sm">{u.systemRoleName}</span>
                  ) : (
                    <UnitRoleCell
                      businessUnitId={u.id}
                      userId={user.id}
                      systemRoleId={u.systemRoleId}
                      systemRoleName={u.systemRoleName}
                      reachesAllUnits={u.access === 'all-units'}
                    />
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {u.access === 'granted' && !readOnly ? (
                    <RevokeUnitAccessButton
                      businessUnitId={u.id}
                      unitName={u.name}
                      userId={user.id}
                      userName={user.name}
                      systemRoleId={u.systemRoleId}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <GrantUnitAccessDialog
        open={granting}
        onOpenChange={setGranting}
        user={{ id: user.id, name: user.name }}
        excludeUnitIds={rows.map((u) => u.id)}
      />
      <ChangePrimaryEmployerDialog
        open={moving}
        onOpenChange={setMoving}
        user={{ id: user.id, name: user.name }}
        currentUnitId={user.businessUnitId}
      />
    </Section>
  );
}
